import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useDocument } from "../hooks/use-document"
import { useKnowledgeBase } from "../hooks/use-knowledge-base"
import { useRedoDocumentMetadata } from "../hooks/use-redo-document-metadata"
import { DocumentPreviewPanel } from "./document-preview-panel"
import { DocumentMetadataPanel } from "./document-metadata-panel"

type Props = {
  docId: string | null
  onClose: () => void
}

const EXTRACTING_POLL_INTERVAL_MS = 10_000

export const DocumentDetailDialog = ({ docId, onClose }: Props) => {
  const [isRedoing, setIsRedoing] = useState(false)
  const sawExtractingRef = useRef(false)

  const [prevDocId, setPrevDocId] = useState(docId)
  if (docId !== prevDocId) {
    setPrevDocId(docId)
    if (!docId) {
      setIsRedoing(false)
    }
  }

  useEffect(() => {
    if (!docId) {
      sawExtractingRef.current = false
    }
  }, [docId])

  const { data, isLoading, isError, error } = useDocument(docId, {
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === "extracting_metadata") return EXTRACTING_POLL_INTERVAL_MS
      if (isRedoing && status !== "completed" && status !== "failed") {
        return EXTRACTING_POLL_INTERVAL_MS
      }
      return false
    },
  })
  const { data: kb } = useKnowledgeBase(data?.kbId ?? "")
  const redoMutation = useRedoDocumentMetadata()

  const isExtracting = data?.status === "extracting_metadata"
  const canRedoMetadata =
    data?.status === "completed" || data?.status === "failed"

  useEffect(() => {
    if (!isRedoing) return
    if (data?.status === "extracting_metadata") {
      sawExtractingRef.current = true
      return
    }
    if (
      sawExtractingRef.current &&
      (data?.status === "completed" || data?.status === "failed")
    ) {
      setIsRedoing(false)
      sawExtractingRef.current = false
    }
  }, [data?.status, isRedoing])

  const handleRedoMetadata = () => {
    if (!docId || redoMutation.isPending) return
    sawExtractingRef.current = false
    setIsRedoing(true)
    redoMutation.mutate(docId, {
      onError: () => {
        setIsRedoing(false)
      },
    })
  }

  return (
    <Dialog
      open={!!docId}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="flex h-[85vh] w-[98vw] max-w-none flex-col gap-4 sm:max-w-none">
        <DialogHeader className="pr-8">
          <DialogTitle className="truncate">
            {data?.name ?? "Document detail"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
          {/* Left: file preview */}
          <div className="h-[60vh] shrink-0 overflow-hidden rounded-md border border-border lg:h-auto lg:w-[72%] lg:shrink lg:rounded-none lg:border-0 lg:border-r">
            {isLoading && (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="size-8 animate-spin text-primary" />
              </div>
            )}
            {docId && data && (
              <DocumentPreviewPanel
                key={docId}
                docId={docId}
                name={data.name}
                documentType={data.documentType}
              />
            )}
          </div>

          {/* Right: metadata */}
          <div className="mt-4 min-h-0 lg:mt-0 lg:w-[28%] lg:overflow-y-auto lg:pl-4">
            {isLoading && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Loading…
              </p>
            )}
            {isError && (
              <p className="py-8 text-center text-sm text-destructive">
                {error instanceof Error
                  ? error.message
                  : "Failed to load document."}
              </p>
            )}
            {data && (
              <DocumentMetadataPanel
                key={data.id}
                doc={data}
                kbName={kb?.name}
                isExtracting={isExtracting}
                isRedoing={isRedoing}
                canRedoMetadata={canRedoMetadata}
                onRedo={handleRedoMetadata}
                redoPending={redoMutation.isPending}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
